<template>
  <div class="i-download-pdf">
    <iOnboardingTooltip
      :value="5"
      :position-left="positionLeft"
    />
    <div
      class="pad-wrap"
      @click="onDownloadClick()"
    >
      <a
        id="gtm-selector-download-pdf-interest"
        class="unlock-download-pdf"
        :class="{ setrow: props.setRow }"
      >
        <img :src="iconDownload" />
        <div class="text">Save</div>
      </a>
    </div>
    <div
      v-if="data.showShade"
      class="shade"
    ></div>

    <iBaseModal
      :visible="data.showRedirectModal"
      @click-on-close="data.showRedirectModal = false"
    >
      <div class="confirmation-modal">
        <div class="title">Mobile download unavailable</div>

        <div>
          Downloading as PDF is not available on mobile and tablet devices, please use a laptop or desktop computer to
          access this feature.
        </div>
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue"
import { useReportStore } from "@/stores/report"

import { GlobalFunctions } from "@/helpers/globalFunctions"

import iBaseModal from "@/components/base/BaseModal.vue"
import iOnboardingTooltip from "@/components/report/OnboardingTooltip.vue"

import iconDownload from "@/assets/icons/icon-downloader.svg"

// PROPS
const props = defineProps({
  setRow: {
    type: Boolean,
    default: false
  }
})

// DATA
const data = reactive({
  showShade: false,
  showRedirectModal: false
})

// STORES
const reportStore = useReportStore()

//  COMPUTED
const activeJob = computed(() => reportStore.getActiveJob)

const positionLeft = computed(() => {
  return (
    GlobalFunctions.isExtendedRecommendedJobType(activeJob.value) &&
    !activeJob.value?.isCurrentJob &&
    !activeJob.value?.isActivelyExploringJob &&
    !activeJob.value?.isSampleJob
  )
})

// FUNCTIONS
const openPrintWindow = () => {
  // display shade to hide scrolling
  data.showShade = true

  // scroll to page end so all radar charts render
  window.scroll({
    top: document.body.scrollHeight,
    left: 0,
    behavior: "smooth"
  })

  // wait 5 ms to allow charts render
  setTimeout(() => {
    window.print()
  }, 600)

  // remove shade and go back to top after print modal closed
  window.onafterprint = () => {
    data.showShade = false
    scroll({
      top: 0,
      left: 0,
      behavior: "instant"
    })
  }
}

const openRedirectModal = () => {
  data.showRedirectModal = true
}

const onDownloadClick = () => {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
    openRedirectModal()
  } else {
    openPrintWindow()
  }
}
</script>

<style scoped lang="scss">
.i-download-pdf {
  position: relative;
  width: 100%;

  .unlock-download-pdf {
    @include flex($direction: column, $justify: center, $align: center, $gap: 2px);

    cursor: pointer;
    margin: auto;

    @media screen and (min-width: $breakpoint-sm) {
      flex-direction: row;
      gap: 8px;
    }

    &.setrow {
      flex-direction: row;
      gap: 8px;
    }

    img {
      width: 18px;
      height: 18px;

      @media screen and (min-width: $breakpoint-sm) {
        align-self: start;
      }
    }

    .text {
      color: $pickle-bluewood900;

      @include text-sm;

      font-weight: 600;
    }
  }

  .shade {
    position: fixed;
    z-index: $z-index-modal;
    width: 100vw;
    height: 100vh;
    background-color: #000000eb;
    left: 0;
    top: 0;
  }

  .confirmation-modal {
    @include flex($direction: column, $justify: start, $align: center, $gap: 25px);

    .title {
      @include display-xs;

      font-weight: 700;
      text-align: center;
    }
  }
}

@media print {
  .i-download-pdf {
    display: none;
  }
}
</style>
